/**
 * IXD 값 편집기 — 본문 스크롤에 맞춰 파일 정보 줄(DSD 파일명·`DSD워크북 보기`·`워크북
 * 변경`)을 **점차** 접는 계산. 아래로 내린 만큼 줄이 밀려 올라가 사라지고, 위로 올린
 * 만큼 그 자리에서 다시 드러난다 — 맨 위까지 올려야 돌아오는 방식이 아니다.
 *
 * 순수 함수로 둔다: 스크롤 핸들러와 본문 remount 보정이 같은 계산을 쓰고, 경계값은
 * DOM 없이 테스트한다.
 */

/** 파일 정보 줄(`SubHeader` 의 h-12) 높이 = 다 접혔을 때 본문에 넘기는 양 */
export const SUB_HEADER_ROW_PX = 48;

/**
 * 이 위치 안쪽은 "맨 위" 로 보고 무조건 완전히 펼친다. 본문 래퍼의 위 여백(py-3 = 12px)
 * 보다 크게 둔다 — 워크북을 붙일 때 첫 표로 보내는 프로그래매틱 스크롤이 딱 그 12px 에
 * 멈추므로, 더 작게 두면 스크롤한 적 없는 사용자에게서 줄과 1회 안내가 사라진다.
 */
export const BODY_SCROLL_TOP_PX = 24;

/** 판정에 필요한 것만 — 테스트에서 DOM 없이 부를 수 있게 좁혀 받는다 */
export type BodyScroll = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
};

export type CollapseState = {
  /** 지금 접혀 있는 양 (0 = 완전히 펼침, 48 = 완전히 접힘) */
  px: number;
  /** 직전 샘플 — 이동량(delta)을 내기 위해 들고 있는다 */
  lastScrollTop: number;
  /** 직전 샘플의 최대 스크롤 — 접힘이 만든 clamp 를 사용자 스크롤과 구분한다 */
  lastMaxScroll: number;
};

export const INITIAL_COLLAPSE: CollapseState = {
  px: 0,
  lastScrollTop: 0,
  lastMaxScroll: 0,
};

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max);

/**
 * 본문 스크롤 한 번을 접힘 양에 반영한다. 이동량을 누적하므로 본문 중간에서 위로
 * 조금만 올려도 그만큼 줄이 다시 드러난다.
 *
 * 두 가지를 걸러낸다:
 * 1. **짧은 문서** — 다 접어도 스크롤이 거의 생기지 않는 문서(오버플로 ≤ 48px)는 접지
 *    않는다. 얻을 자리가 없는데 줄만 깜빡인다.
 * 2. **접힘이 만든 clamp** — 줄을 접으면 본문 `clientHeight` 가 그만큼 늘어 최대 스크롤이
 *    줄고, 맨 아래에 붙어 있었다면 브라우저가 `scrollTop` 을 끌어내린다. 그 감소를
 *    "위로 올렸다" 로 읽으면 접힘↔펼침이 입력 없이 진동한다.
 */
export const nextCollapse = (
  prev: CollapseState,
  body: BodyScroll,
): CollapseState => {
  const maxScroll = Math.max(body.scrollHeight - body.clientHeight, 0);
  const sampled = { lastScrollTop: body.scrollTop, lastMaxScroll: maxScroll };
  const delta = body.scrollTop - prev.lastScrollTop;

  // 맨 위 근처 — 항상 완전히 펼친다
  if (body.scrollTop <= BODY_SCROLL_TOP_PX) return { px: 0, ...sampled };

  // 다 펼친 상태의 오버플로. 접힌 만큼은 이미 본문이 먹었으므로 되돌려 더한다.
  if (maxScroll + prev.px <= SUB_HEADER_ROW_PX) return { px: 0, ...sampled };

  // 맨 아래에 붙은 채 scrollTop 이 줄었다 = 접힘이 자리를 먹어 clamp 된 것
  const clampedByCollapse = delta < 0 && body.scrollTop >= maxScroll;
  if (clampedByCollapse) return { px: prev.px, ...sampled };

  return { px: clamp(prev.px + delta, SUB_HEADER_ROW_PX), ...sampled };
};

/** 본문(스크롤 컨테이너)이 없는 분기에서는 접힘을 들고 있을 근거가 없다 */
export const collapseFromBody = (
  prev: CollapseState,
  body: BodyScroll | null,
): CollapseState => (body ? nextCollapse(prev, body) : INITIAL_COLLAPSE);
